'use client';

import posthog from 'posthog-js';
import { PostHogProvider as PHProvider } from 'posthog-js/react';
import { useEffect } from 'react';

export function PostHogProvider({ children }: { children: React.ReactNode }) {
  useEffect(() => {
    const posthogKey = process.env.NEXT_PUBLIC_POSTHOG_KEY;
    const posthogHost = process.env.NEXT_PUBLIC_POSTHOG_HOST;
    if (!posthogKey || !posthogHost) return;

    const init = () => {
      posthog.init(posthogKey, {
        api_host: posthogHost,
        person_profiles: 'identified_only',
        capture_pageview: false, // We'll capture pageviews manually
        capture_pageleave: true,
        // Skip loading the PostHog session-recorder (~51 KiB) and surveys
        // (~32 KiB) feature scripts. Re-enable individually if needed.
        disable_session_recording: true,
        disable_surveys: true,
        loaded: (posthog) => {
          if (process.env.NODE_ENV === 'development') {
            posthog.debug();
          }
        },
      });
    };

    // Defer init until the browser is idle so analytics doesn't compete with
    // hero paint / hydration. Fallback: short timeout for browsers without
    // requestIdleCallback.
    const ric = (window as any).requestIdleCallback as
      | ((cb: () => void, opts?: { timeout?: number }) => number)
      | undefined;
    const handle = ric
      ? ric(init, { timeout: 2000 })
      : window.setTimeout(init, 1500);

    return () => {
      if (ric && typeof (window as any).cancelIdleCallback === "function") {
        (window as any).cancelIdleCallback(handle);
      } else {
        window.clearTimeout(handle as number);
      }
    };
  }, []);

  return <PHProvider client={posthog}>{children}</PHProvider>;
}

